import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CalendarDays, DoorOpen, Users } from 'lucide-react';
import type { ProgrammaEvento } from '@eventi/tipi.ts';
import { ConfirmDeleteDialog } from '../ui/confirm-delete-dialog';
import { useNotifications } from '../../store/useStore';
import { cn } from '../../lib/cn';
import { eventiService } from '../../lib/eventi/eventiService';
import { messaggioErrore } from '../../lib/eventi/errori';
import { periodoEvento } from '../../lib/eventi/riepilogo';
import type { Evento } from '../../lib/eventi/tipi';
import { Interruttore } from './Interruttore';
import { TesseraData } from './TesseraData';

type Props = {
  evento: Evento;
  programma: ProgrammaEvento;
  partecipanti: number;
  onEventoCambiato: () => Promise<void>;
};

/** Il link per tornare all'elenco: c'e' anche quando l'evento non si apre. */
export function TornaAgliEventi() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate('/eventi')}
      className="mb-3 inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-800"
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      Tutti gli eventi
    </button>
  );
}

/**
 * La testata di un evento: quando si tiene, quanti partecipano, e
 * l'interruttore per aprire o chiudere le richieste, che si usa durante la
 * vita dell'evento e per questo sta sempre in vista (non nelle impostazioni).
 */
export function TestataEvento({ evento, programma, partecipanti, onEventoCambiato }: Props) {
  const { addNotification } = useNotifications();
  const [chiedi, setChiedi] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const aperte = evento.scelte_aperte;
  const periodo = periodoEvento(programma.giorni);

  const cambia = async () => {
    setSalvando(true);
    try {
      await eventiService.impostaRichiesteAperte(evento.id, !aperte);
      await onEventoCambiato();
      addNotification({ type: 'success', title: aperte ? 'Richieste chiuse' : 'Richieste aperte' });
      setChiedi(false);
    } catch (e) {
      addNotification({ type: 'error', title: 'Non riesco a cambiare le richieste', message: messaggioErrore(e) });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <header>
      <TornaAgliEventi />
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <TesseraData giorni={programma.giorni} />
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">{evento.nome}</h1>
            <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-500">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4" />
                {periodo ?? 'Date da definire'}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                {partecipanti} {partecipanti === 1 ? 'partecipante' : 'partecipanti'}
              </span>
              {programma.aule.length > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <DoorOpen className="h-4 w-4" />
                  {programma.aule.length} {programma.aule.length === 1 ? 'aula' : 'aule'}
                </span>
              )}
            </p>
          </div>
        </div>

        <div
          className={cn(
            'rounded-xl border px-4 py-3 lg:w-80 flex-shrink-0 transition-colors',
            aperte ? 'border-green-200 bg-green-50/70' : 'border-gray-200 bg-white',
          )}
        >
          <Interruttore
            acceso={aperte}
            onCambia={() => setChiedi(true)}
            etichetta={aperte ? 'Richieste aperte' : 'Richieste chiuse'}
            spiegazione={aperte ? 'I partecipanti possono scegliere il loro percorso.' : 'La pagina di scelta non accetta richieste.'}
          />
        </div>
      </div>

      <ConfirmDeleteDialog
        open={chiedi}
        onOpenChange={(v) => !v && !salvando && setChiedi(false)}
        onConfirm={() => void cambia()}
        isDeleting={salvando}
        title={aperte ? 'Chiudere le richieste?' : 'Aprire le richieste?'}
        description={
          aperte
            ? "La pagina di scelta risponderà che le richieste sono chiuse. Chi ha già inviato la sua non perde niente; potete riaprirle quando volete."
            : 'La pagina di scelta torna ad accettare le richieste: chi ha il link personale può scegliere il suo percorso.'
        }
        confirmLabel={aperte ? 'Chiudi le richieste' : 'Apri le richieste'}
        busyLabel="Salvo…"
        confirmClassName="bg-primary-600 hover:bg-primary-700 text-white"
      />
    </header>
  );
}
